import { useCallback, useEffect, useState } from "react";
import { CheckSquare, ListChecks, Plus, Square, Trash2 } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";

export const AdminOnboardingTab = () => {
    const [data, setData] = useState(null);
    const [newTask, setNewTask] = useState({});
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const flash = (msg) => {
        setNotice(msg);
        setTimeout(() => setNotice(""), 8000);
    };

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/onboarding");
            setData(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const toggle = async (task) => {
        try {
            const { data: resp } = await platformClient.patch(`/admin/onboarding/tasks/${task.id}`, { done: !task.done });
            if (resp.invoice) {
                flash(`Factuur ${resp.invoice.number} aangemaakt — verstuur hem via Abonnementen & facturatie`);
            }
            load();
        } catch {}
    };

    const addTask = async (tenantId) => {
        const title = (newTask[tenantId] || "").trim();
        if (!title) return;
        setError("");
        try {
            await platformClient.post(`/admin/onboarding/${tenantId}/tasks`, { title });
            setNewTask((s) => ({ ...s, [tenantId]: "" }));
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const removeTask = async (task) => {
        try {
            await platformClient.delete(`/admin/onboarding/tasks/${task.id}`);
            load();
        } catch {}
    };

    const start = async (tenantId) => {
        setError("");
        try {
            await platformClient.post(`/admin/onboarding/${tenantId}/start`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    if (!data) {
        return <p data-testid="onboarding-loading" className="mt-10 text-xs uppercase tracking-[0.3em] text-ash">Checklists laden…</p>;
    }

    return (
        <div data-testid="admin-onboarding-tab" className="mt-8">
            <p className="max-w-2xl text-sm leading-relaxed text-ash">
                Elke klant die een offerte accepteert krijgt automatisch deze checklist. Zo valt geen enkele nieuwe
                klant tussen wal en schip.
            </p>

            {error ? (
                <p data-testid="onboarding-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">{error}</p>
            ) : null}
            {notice ? (
                <p data-testid="onboarding-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p>
            ) : null}

            {data.without_checklist.length ? (
                <div className="mt-6 border border-gold/30 bg-gold/5 p-5">
                    <p className={LABEL}>Actieve klanten zonder checklist</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                        {data.without_checklist.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`onboarding-start-${t.id}`}
                                onClick={() => start(t.id)}
                                className="flex items-center gap-2 border border-gold/50 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                            >
                                <ListChecks className="h-3.5 w-3.5" /> Start checklist — {t.name}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="mt-6 space-y-4">
                {data.boards.map((b) => (
                    <div key={b.tenant_id} data-testid={`onboarding-board-${b.tenant_id}`} className="border border-white/10 bg-coal p-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="text-sm text-bone">{b.name}</p>
                                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ash">
                                    {b.done} van {b.total} klaar{b.source_quote ? ` · vanuit offerte ${b.source_quote}` : ""}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="h-1.5 w-40 bg-white/10">
                                    <div
                                        className="h-full bg-gold transition-all duration-500"
                                        style={{ width: `${b.total ? (b.done / b.total) * 100 : 0}%` }}
                                    />
                                </div>
                                <span className={`text-xs ${b.done === b.total ? "text-gold" : "text-ash"}`}>
                                    {b.done === b.total ? "Afgerond" : `${Math.round((b.done / Math.max(1, b.total)) * 100)}%`}
                                </span>
                            </div>
                        </div>
                        <div className="mt-5 space-y-1.5">
                            {b.tasks.map((task) => (
                                <div key={task.id} data-testid={`onboarding-task-${task.id}`} className="group flex items-center gap-3">
                                    <button
                                        data-testid={`onboarding-toggle-${task.id}`}
                                        onClick={() => toggle(task)}
                                        aria-label={task.done ? "Markeer als niet gedaan" : "Markeer als gedaan"}
                                        className="shrink-0 text-ash transition-colors hover:text-gold"
                                    >
                                        {task.done ? <CheckSquare className="h-4 w-4 text-gold" /> : <Square className="h-4 w-4" />}
                                    </button>
                                    <span className={`flex-1 text-sm ${task.done ? "text-ash line-through" : "text-bone"}`}>
                                        {task.title}
                                        {task.action === "setup_invoice" && !task.invoice_number ? (
                                            <span className="ml-2 border border-gold/40 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] text-gold no-underline">
                                                maakt factuur bij afvinken
                                            </span>
                                        ) : null}
                                        {task.invoice_number ? (
                                            <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-gold">
                                                factuur {task.invoice_number}
                                            </span>
                                        ) : null}
                                    </span>
                                    <button
                                        data-testid={`onboarding-delete-${task.id}`}
                                        onClick={() => removeTask(task)}
                                        aria-label="Taak verwijderen"
                                        className="text-ash/30 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex gap-3">
                            <input
                                data-testid={`onboarding-newtask-${b.tenant_id}`}
                                value={newTask[b.tenant_id] || ""}
                                onChange={(e) => setNewTask((s) => ({ ...s, [b.tenant_id]: e.target.value }))}
                                placeholder="Eigen taak toevoegen…"
                                maxLength={120}
                                className={`${inputCls} mt-0 max-w-xs`}
                                onKeyDown={(e) => e.key === "Enter" && addTask(b.tenant_id)}
                            />
                            <button
                                data-testid={`onboarding-add-${b.tenant_id}`}
                                onClick={() => addTask(b.tenant_id)}
                                className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                            >
                                <Plus className="h-3 w-3" /> Voeg toe
                            </button>
                        </div>
                    </div>
                ))}
                {data.boards.length === 0 ? (
                    <p data-testid="onboarding-empty" className="text-sm text-ash">
                        Nog geen checklists — ze ontstaan automatisch zodra een klant een offerte accepteert.
                    </p>
                ) : null}
            </div>
        </div>
    );
};
